"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const Base_1 = __importDefault(require("./Base"));
const CanonicalBridge_1 = __importDefault(require("./CanonicalBridge"));
const CanonicalWatcher_1 = __importDefault(require("./watchers/CanonicalWatcher"));
const HopBridge_1 = __importDefault(require("./HopBridge"));
const Watcher_1 = __importDefault(require("./watchers/Watcher"));
const version_1 = __importDefault(require("./version"));
const models_1 = require("./models");
const BaseWatcher_1 = require("./watchers/BaseWatcher");
/**
 * Class reprensenting Hop
 * @namespace Hop
 */
class Hop extends Base_1.default {
    /**
     * @desc Instantiates Hop SDK.
     * Returns a new Hop SDK instance.
     * @param networkOrOptionsObject - L1 network name (e.g. 'mainnet', 'kovan', 'goerli')
     * @param signer - Ethers `Signer` for signing transactions.
     * @returns New Hop SDK instance.
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *
     *const hop = new Hop('mainnet')
     *```
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *import { Wallet } from 'ethers'
     *
     *const signer = new Wallet(privateKey)
     *const hop = new Hop('mainnet', signer)
     *```
     */
    // eslint-disable-next-line no-useless-constructor
    constructor(networkOrOptionsObject, signer, chainProviders) {
        super(networkOrOptionsObject, signer, chainProviders);
        /** Event enum */
        this.Event = BaseWatcher_1.Event;
        /** Chain class */
        this.Chain = models_1.Chain;
        /** Token class */
        this.Token = models_1.Token;
        this.priceFeedApiKeys = null;
    }
    /**
     * @desc Returns a bridge set instance.
     * @param token - Token model or symbol of token of bridge to use.
     * @returns A HopBridge instance.
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *const bridge = hop.bridge('USDC')
     *```
     */
    bridge(token) {
        const hopBridge = new HopBridge_1.default({
            network: this.network,
            signer: this.signer,
            token,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            customCoreConfigJsonUrl: this.customCoreConfigJsonUrl,
            customAvailableLiquidityJsonUrl: this.customAvailableLiquidityJsonUrl,
            blocklist: this.blocklist
        });
        // port over exiting properties
        if (this.priceFeedApiKeys) {
            hopBridge.priceFeed.setApiKeys(this.priceFeedApiKeys);
        }
        hopBridge.baseConfigUrl = this.baseConfigUrl;
        hopBridge.configFileFetchEnabled = this.configFileFetchEnabled;
        return hopBridge;
    }
    /**
     * @desc Returns a canonical bridge sdk instance.
     * @param token - Token model or symbol of token of canonical bridge to use.
     * @param chain - Chain model.
     * @returns A CanonicalBridge instance.
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *const bridge = hop.canonicalBridge('USDC')
     *```
     */
    canonicalBridge(token, chain) {
        return new CanonicalBridge_1.default({
            network: this.network,
            signer: this.signer,
            token,
            chain,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist
        });
    }
    /**
     * @desc Returns hop instance with signer connected. Used for adding or changing signer.
     * @param signer - Ethers `Signer` for signing transactions.
     * @returns A new Hop SDK instance with connected Ethers Signer.
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *import { Wallet } from 'ethers'
     *
     *const signer = new Wallet(privateKey)
     *let hop = new Hop()
     * // ...
     *hop = hop.connect(signer)
     *```
     */
    connect(signer) {
        this.signer = signer;
        return new Hop({
            network: this.network,
            signer,
            chainProviders: this.chainProviders,
            baseConfigUrl: this.baseConfigUrl,
            configFileFetchEnabled: this.configFileFetchEnabled,
            blocklist: this.blocklist
        });
    }
    /**
     * @desc Returns the SDK version.
     * @returns version string
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     *console.log(hop.version)
     *```
     */
    get version() {
        return version_1.default;
    }
    /**
     * @desc Watches for Hop transaction events.
     * @param txHash - Source transaction hash.
     * @param token - Token name or model.
     * @param sourceChain - Source chain name or model.
     * @param destinationChain - Destination chain name or model.
     * @example
     *```js
     *import { Hop } from '@hop-protocol/sdk'
     *
     *const hop = new Hop()
     * hop
     *   .watch(tx.hash, 'USDC', Chain.Ethereum, Chain.Gnosis)
     *   .on('receipt', ({receipt, chain}) => {
     *     console.log(chain.Name, receipt)
     *   })
     *```
     */
    watch(txHash, token, sourceChain, destinationChain, isCanonicalTransfer = false, options = {}) {
        // TODO: detect type of transfer
        return isCanonicalTransfer
            ? this.watchCanonical(txHash, token, sourceChain, destinationChain)
            : this.watchBridge(txHash, token, sourceChain, destinationChain, options);
    }
    watchBridge(txHash, token, sourceChain, destinationChain, options = {}) {
        token = this.toTokenModel(token);
        sourceChain = this.toChainModel(sourceChain);
        destinationChain = this.toChainModel(destinationChain);
        return new Watcher_1.default({
            network: this.network,
            signer: this.signer,
            sourceTxHash: txHash,
            token: token,
            sourceChain: sourceChain,
            destinationChain: destinationChain,
            chainProviders: this.chainProviders,
            options
        }).watch();
    }
    watchCanonical(txHash, token, sourceChain, destinationChain) {
        return new CanonicalWatcher_1.default({
            network: this.network,
            signer: this.signer,
            sourceTxHash: txHash,
            token: token,
            sourceChain: sourceChain,
            destinationChain: destinationChain
        }).watch();
    }
    setPriceFeedApiKeys(apiKeys = {}) {
        this.priceFeedApiKeys = apiKeys;
    }
}
/** Event enum */
Hop.Event = BaseWatcher_1.Event;
/** Chain class */
Hop.Chain = models_1.Chain;
/** Token class */
Hop.Token = models_1.Token;
exports.default = Hop;
